import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

const firebaseConfig = {
  apiKey: "AIzaSyBvUXAwyiCGgy78sXj6f9XqDD4jFcYySiU",
  authDomain: "guesttrackercloud.firebaseapp.com",
  projectId: "guesttrackercloud",
  storageBucket: "guesttrackercloud.firebasestorage.app",
  messagingSenderId: "553817324987",
  appId: "1:553817324987:web:7edc0b078257f910e7589e",
  measurementId: "G-43M490JBXX"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

// Initialize services with US region for functions
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app, 'us-central1');

// ✅ CONNECT TO EMULATOR IN DEVELOPMENT
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATOR === 'true') {
  try {
    connectFunctionsEmulator(functions, 'localhost', 5001);
    console.log('🔧 Connected to Firebase Functions Emulator');
  } catch (error) {
    console.log('⚠️ Functions emulator not available, using production');
  }
}

console.log('🔧 Firebase initialized with us-central1 functions');

// Export services
export const getFirebaseServices = () => ({
  auth,
  db,
  functions
});

// Create a separate instance for public access (no auth state)
export const getPublicFirebaseServices = () => ({
  db,
  functions
});

// ✅ AUTHENTICATION FUNCTIONS
export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('Login error:', error);
    let errorMessage = 'Login failed. Please try again.';
    
    switch (error.code) {
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address.';
        break;
      case 'auth/user-disabled':
        errorMessage = 'This account has been disabled.';
        break;
      case 'auth/user-not-found':
        errorMessage = 'No account found with this email.';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Incorrect password.';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many failed attempts. Please try again later.';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your connection.';
        break;
      default:
        errorMessage = error.message;
    }
    
    return { success: false, error: errorMessage };
  }
};

export const logout = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Logout error:', error);
    return { success: false, error: error.message };
  }
};

export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// ✅ UPDATED EMAIL FUNCTION TO USE DIRECT HTTP
export const sendInvoiceEmail = async (customerEmail, invoiceData) => {
  try {
    console.log('📧 Sending invoice email via direct HTTP...');
    
    // Get current user's ID token for authentication
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    
    const response = await fetch('https://us-central1-guesttrackercloud.cloudfunctions.net/sendInvoiceEmail', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': idToken ? `Bearer ${idToken}` : '',
      },
      body: JSON.stringify({
        customerEmail,
        invoiceData
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Email sent successfully:', result);
    return result;
    
  } catch (error) {
    console.error('❌ Send email error:', error);
    throw new Error(`Email failed: ${error.message}`);
  }
};

// ✅ USER MANAGEMENT FUNCTIONS
export const createUser = async (userData) => {
  try {
    const createUserFunction = httpsCallable(functions, 'createUser');
    const result = await createUserFunction(userData);
    return result.data;
  } catch (error) {
    console.error('Create user error:', error);
    throw error;
  }
};

export const getUsers = async () => {
  try {
    const getUsersFunction = httpsCallable(functions, 'getUsers');
    const result = await getUsersFunction();
    return result.data;
  } catch (error) {
    console.error('Get users error:', error);
    throw error;
  }
};

export const deleteUser = async (uid) => {
  try {
    const deleteUserFunction = httpsCallable(functions, 'deleteUser');
    const result = await deleteUserFunction({ uid });
    return result.data;
  } catch (error) {
    console.error('Delete user error:', error);
    throw error;
  }
};

export const updateUserRole = async (uid, role) => {
  try {
    const updateUserRoleFunction = httpsCallable(functions, 'updateUserRole');
    const result = await updateUserRoleFunction({ uid, role });
    return result.data;
  } catch (error) {
    console.error('Update user role error:', error);
    throw error;
  }
};

export const setFirstAdmin = async (email) => {
  try {
    const setFirstAdminFunction = httpsCallable(functions, 'setFirstAdmin');
    const result = await setFirstAdminFunction({ email });
    return result.data;
  } catch (error) {
    console.error('Set first admin error:', error);
    throw error;
  }
};

// Get current user's role from custom claims
export const getCurrentUserRole = async () => {
  try {
    if (!auth.currentUser) return null;
    const idTokenResult = await auth.currentUser.getIdTokenResult();
    return idTokenResult.claims.role || null;
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
};

// Check if current user is admin
export const isCurrentUserAdmin = async () => {
  const role = await getCurrentUserRole();
  return role === 'admin';
};

// Export the instances directly as well
export { auth, db, functions, analytics };